<div id="report-contents" #reportContentsRef class="w-full">
	@if (isPfPmLayout) {
		<app-ts1-pfpm-report-layout
			[layoutKey]="pfPmLayoutKey"
			[reportSummary]="reportSummary"
			[candidateId]="candidateId ?? ''"
		></app-ts1-pfpm-report-layout>
	} @else {
		<div class="my-8 flex items-center justify-between px-0 lg:px-6">
			<h2 class="text-center !text-lg !font-bold !leading-6">{{ summaryReportTitle }}</h2>
			<div class="flex size-28 items-center justify-center">
				@if (logo) {
					<img [src]="logo" alt="AOC" class="scale-150" />
				}
			</div>
		</div>

		<mat-stepper id="report-summary-stepper" #stepper>
			<!-- STEP 1: Summary -->
			<mat-step>
				<div id="summary-report" #summaryReport>
					<div class="avoid-break break-after">
						<!-- Training Details -->
						<div id="training-details" class="grid grid-cols-2 gap-8 p-8 pt-4">
							<div class="form-display flex flex-col gap-2">
								<label class="form-label">Training Date</label>
								<div class="form-value">
									{{ formatSummaryDate($safeNavigationMigration(reportSummary?.trainingDate)) }}
								</div>
							</div>

							<div class="session-selection-field form-display flex flex-col gap-2">
								<label class="form-label">Session</label>
								<div class="form-value">
									@let sessions = sessionConfigurations(reportSummary);
									@if (sessions.length) {
										@for (cfg of sessions; track cfg.description; let last = $last) {
											<span class="session-summary-name">{{ cfg.description }}</span>
											@if (cfg.selectionRemarks) {
												<span class="session-summary-remarks"> : {{ cfg.selectionRemarks }}</span>
											}
											@if (!last) {
												<span>, </span>
											}
										}
									} @else {
										—
									}
								</div>
							</div>
						</div>

						<!-- Candidate Details -->
						<div class="grid grid-rows-1 gap-4">
							<div
								class="flex h-[40px] items-center justify-start bg-blue-20 px-4 text-lg font-bold text-black lg:bg-violet-light lg:text-violet"
							>
								Candidate Details
							</div>
						</div>
						<div class="grid grid-cols-2 gap-8 p-8 pt-4">
							<div class="form-display flex flex-col gap-2">
								<label class="form-label">Name</label>
								<div class="form-value">
									{{ reportSummary?.candidateDetails?.name ?? "-" }}
								</div>
							</div>

							<div class="form-display flex flex-col gap-2">
								<label class="form-label">Rank</label>
								<div class="form-value">
									{{ reportSummary?.candidateDetails?.rank ?? "-" }}
								</div>
							</div>

							<div class="form-display flex flex-col gap-2">
								<label class="form-label">Staff Number</label>
								<div class="form-value">
									{{ reportSummary?.candidateDetails?.staffNumber ?? "-" }}
								</div>
							</div>

							@if (!isCC) {
								@if (isTS1) {
									<div class="form-display flex flex-col gap-2">
										<label class="form-label">Trainee Profile</label>
										<div class="form-value">
											{{ reportSummary?.candidateDetails?.trainingProfile || "-" }}
										</div>
									</div>
									<div class="form-display flex flex-col gap-2">
										<label class="form-label">License Type</label>
										<div class="form-value">
											{{ reportSummary?.candidateDetails?.licenseType || "-" }}
										</div>
									</div>
								} @else {
									<div class="form-display flex flex-col gap-2">
										<label class="form-label">License Type</label>
										<div class="form-value">
											{{ reportSummary?.candidateDetails?.licenseType || "-" }}
										</div>
									</div>
								}
							}

							@if (!isCC) {
								<div class="form-display flex flex-col gap-2">
									<label class="form-label">License Number</label>
									<div class="form-value">
										{{ reportSummary?.candidateDetails?.licenseNumber || "-" }}
									</div>
								</div>

								<div class="form-display flex flex-col gap-2">
									<label class="form-label">License Expiry Date</label>
									<div class="form-value">
										{{
											reportSummary?.candidateDetails?.licenseNumber
												? formatSummaryDate(
														$safeNavigationMigration(reportSummary?.candidateDetails?.licenseExpiryDate)
													)
												: "-"
										}}
									</div>
								</div>
							}
						</div>

						<!-- Flight / Simulator Details -->
						@if (!isCC && !isTS6) {
							<app-flight-simulator-summary-fields-v2
								[details]="$safeNavigationMigration(reportSummary?.flightSimulatorDetails)"
								[ets1SessionVersion]="reportSummary?.ets1SessionVersion"
							/>
						}

						@if (!isTS5 && !isTS6) {
							<!-- Session Details (ratings A–D) -->
							<div class="grid grid-rows-1 gap-4">
								<div
									class="flex h-[40px] items-center justify-start bg-blue-20 px-4 text-lg font-bold text-black lg:bg-violet-light lg:text-violet"
								>
									Session Details
								</div>
							</div>
						}

						@if (!isCC && !isTS6 && !isTS5) {
							<div
								class="mt-4 grid grid-cols-2 gap-x-10 gap-y-6 bg-[#F6FAFF] p-8 lg:gap-x-16 lg:gap-y-16 lg:bg-[#FBFCFE]"
							>
								<div>
									<div class="rating-label">Grading A : How Many</div>
									<div class="rating-box">
										<div
											[class.active]="
												$safeNavigationMigration(reportSummary?.sessionDetails?.ratingA) === null ||
												reportSummary?.sessionDetails?.ratingA === 1
											"
											class="rating-box-item"
										>
											1
										</div>
										<div [class.active]="reportSummary?.sessionDetails?.ratingA === 2" class="rating-box-item">2</div>
										<div [class.active]="reportSummary?.sessionDetails?.ratingA === 3" class="rating-box-item">3</div>
										<div [class.active]="reportSummary?.sessionDetails?.ratingA === 4" class="rating-box-item">4</div>
										<div [class.active]="reportSummary?.sessionDetails?.ratingA === 5" class="rating-box-item">5</div>
									</div>
								</div>

								<div>
									<div class="rating-label">Grading B : How Often</div>
									<div class="rating-box">
										<div
											[class.active]="
												$safeNavigationMigration(reportSummary?.sessionDetails?.ratingB) === null ||
												reportSummary?.sessionDetails?.ratingB === 1
											"
											class="rating-box-item"
										>
											1
										</div>
										<div [class.active]="reportSummary?.sessionDetails?.ratingB === 2" class="rating-box-item">2</div>
										<div [class.active]="reportSummary?.sessionDetails?.ratingB === 3" class="rating-box-item">3</div>
										<div [class.active]="reportSummary?.sessionDetails?.ratingB === 4" class="rating-box-item">4</div>
										<div [class.active]="reportSummary?.sessionDetails?.ratingB === 5" class="rating-box-item">5</div>
									</div>
								</div>

								<div>
									<div class="rating-label">Grading C : TEM Safety Outcome</div>
									<div class="rating-box">
										<div
											[class.active]="
												$safeNavigationMigration(reportSummary?.sessionDetails?.ratingC) === null ||
												reportSummary?.sessionDetails?.ratingC === 1
											"
											class="rating-box-item"
										>
											1
										</div>
										<div [class.active]="reportSummary?.sessionDetails?.ratingC === 2" class="rating-box-item">2</div>
										<div [class.active]="reportSummary?.sessionDetails?.ratingC === 3" class="rating-box-item">3</div>
										<div [class.active]="reportSummary?.sessionDetails?.ratingC === 4" class="rating-box-item">4</div>
										<div [class.active]="reportSummary?.sessionDetails?.ratingC === 5" class="rating-box-item">5</div>
									</div>
								</div>

								<div class="final-rating">
									<div class="rating-label">Grading D : Final Grading</div>
									<div class="rating-box">
										<div
											[class.active]="
												$safeNavigationMigration(reportSummary?.sessionDetails?.finalRating) === null ||
												reportSummary?.sessionDetails?.finalRating === 1
											"
											class="rating-box-item"
										>
											1
										</div>
										<div [class.active]="reportSummary?.sessionDetails?.finalRating === 2" class="rating-box-item">
											2
										</div>
										<div [class.active]="reportSummary?.sessionDetails?.finalRating === 3" class="rating-box-item">
											3
										</div>
										<div [class.active]="reportSummary?.sessionDetails?.finalRating === 4" class="rating-box-item">
											4
										</div>
										<div [class.active]="reportSummary?.sessionDetails?.finalRating === 5" class="rating-box-item">
											5
										</div>
										<div class="rating-border"></div>
									</div>
								</div>
							</div>
						} @else if (isCC) {
							<div class="flex items-center justify-center bg-[#F6FAFF] py-16 lg:bg-[#FBFCFE]">
								<div class="mx-auto">
									<div class="rating-box">
										<div
											class="rating-box-item"
											[ngClass]="{
												'!bg-[#FF5151] !text-white':
													!reportSummary?.sessionDetails?.ratingC || reportSummary?.sessionDetails?.ratingC === 1,
											}"
										>
											1
										</div>

										<div
											class="rating-box-item"
											[ngClass]="{
												'!bg-[#F9AC51] !text-white': reportSummary?.sessionDetails?.ratingC === 2,
											}"
										>
											2
										</div>

										<div
											class="rating-box-item"
											[ngClass]="{
												'!bg-[#FFC738] !text-white': reportSummary?.sessionDetails?.ratingC === 3,
											}"
										>
											3
										</div>

										<div
											class="rating-box-item"
											[ngClass]="{
												'!bg-[#52C280] !text-white': reportSummary?.sessionDetails?.ratingC === 4,
											}"
										>
											4
										</div>

										<div
											class="rating-box-item"
											[ngClass]="{
												'!bg-[#49A06C] !text-white': reportSummary?.sessionDetails?.ratingC === 5,
											}"
										>
											5
										</div>
									</div>
								</div>
							</div>
						}

						@if (
							isRecommendationFormType($safeNavigationMigration(reportSummary?.formType)) &&
								reportSummary?.recommendation;
							as rec
						) {
							@let isTraining = isRecommendationTraining(rec);
							<div class="mt-6">
								<div class="grid grid-rows-1 gap-4">
									<div
										class="flex h-[40px] items-center justify-start bg-blue-20 px-4 text-lg font-bold text-black lg:bg-violet-light lg:text-violet"
									>
										Recommendation by Instructor/Examiner
									</div>
								</div>
								<div class="grid grid-cols-2 gap-8 p-8 pt-4">
									<div class="form-display flex flex-col gap-2">
										<label class="form-label">Is this a training or check session?</label>
										<div class="form-value">
											{{ recommendationSessionLabel(rec) }}
										</div>
									</div>
									<div class="form-display flex flex-col gap-2">
										<label class="form-label"> {{ isTraining ? "Training" : "Check" }} Session Completed </label>
										<div class="form-value">
											{{ formatRecommendationYesNo($safeNavigationMigration(rec?.is_complete)) }}
										</div>
									</div>
									<div class="form-display flex flex-col gap-2">
										<label class="form-label"> {{ isTraining ? "Training" : "Check" }} Objectives Are Met </label>
										<div class="form-value">
											{{ formatRecommendationObjective(rec) }}
										</div>
									</div>
									@if (isTraining) {
										<div class="form-display flex flex-col gap-2">
											<label class="form-label">Cleared for Check</label>
											<div class="form-value">
												{{ formatRecommendationClearance(rec) }}
											</div>
										</div>
									}
								</div>
							</div>
						}

						<!-- Instructor sign-off; hidden in remote sign-off checklist preview -->
						@if (!checklistMode) {
							<div class="mt-6 grid grid-cols-2 gap-8 p-8">
								<div class="space-y-8">
									<div class="form-display flex flex-col gap-2">
										<label class="form-label">Instructor Name</label>
										<div class="form-value">
											{{ reportSummary?.sessionDetails?.instructorName }}
										</div>
									</div>
									@if (!isCC) {
										<div class="form-display flex flex-col gap-2">
											<label class="form-label">License Number</label>
											<div class="form-value">
												{{ reportSummary?.examinerNumber ?? "-" }}
											</div>
										</div>
									}
								</div>
								<div class="form-display flex flex-col gap-2">
									<label class="form-label">Instructor Signature</label>
									<div class="flex h-24 w-72 flex-col items-center gap-4 bg-[#F6FAFF]">
										@if (instructorSignature) {
											<img [src]="instructorSignature" alt="Signature" class="h-full" />
										}
										<span class="text-sm font-medium">{{ formatSignatureTime(reportSummary?.signOffDate) }}</span>
									</div>
								</div>
							</div>

							<div class="my-4 h-0.5 w-full bg-[#CFE3FF]"></div>
						}

						<!-- Candidate signature (TS-1 / ETS-3 / CC); hidden in remote sign-off checklist preview -->
						@if ((isTS1 || isTS3 || isCC) && !checklistMode) {
							<div class="grid grid-cols-2 gap-8 p-8">
								<div>
									<div class="form-display flex flex-col gap-2">
										<label class="form-label">Candidate name</label>
										<div class="form-value">
											{{ reportSummary?.candidateDetails?.name }}
										</div>
									</div>
								</div>

								<div class="form-display flex flex-col gap-2">
									<label class="form-label">Candidate Signature</label>
									<div class="flex h-24 w-72 flex-col items-center gap-4 bg-[#F6FAFF]">
										@if (candidateSignature) {
											<img [src]="candidateSignature" alt="Signature" class="h-full" />
										}
										<span class="text-sm font-medium">{{
											formatSignatureTime(reportSummary?.sessionDetails?.completedOn || reportSummary?.signOffDate)
										}}</span>
									</div>
								</div>
							</div>
						}

						<!-- Instructor Notes/Comments -->
						<div id="instructor-notes" #instructorNotes class="avoid-break break-after mt-5">
							<div
								class="mb-6 flex h-[40px] items-center justify-start bg-blue-20 px-4 text-lg font-bold text-black lg:bg-violet-light lg:text-violet"
							>
								Instructor Notes/Comments
							</div>

							<div class="mt-8">
								<div class="text-sm font-medium text-grey-60">Summary</div>

								<div class="mt-4 min-h-40 rounded-md border border-blue-20 px-8 py-6 text-[85%] uppercase">
									<app-cbta-rating-summary-comments [comments]="comments"></app-cbta-rating-summary-comments>
								</div>
							</div>

							<div class="mt-8">
								<div class="text-sm font-medium text-grey-60">Other Training & Observations</div>

								<div class="mt-4 min-h-40 rounded-md border border-blue-20 px-8 py-6">
									@for (comment of otherComments; track comment) {
										@if (isTS1) {
											@if (!helperService.isEmptyComment(comment)) {
												<p class="mb-1 whitespace-pre-wrap break-words text-left font-medium uppercase">
													{{ comment + `\n` }}
												</p>
											}
										} @else {
											<p class="mb-1 whitespace-pre-wrap break-words text-left font-medium uppercase">
												{{ comment + `\n` || "" | uppercase }}
											</p>
										}
									}
								</div>
							</div>
						</div>
					</div>
				</div>
			</mat-step>

			@if (isTS1 || isTS3 || isCC) {
				<mat-step>
					<div
						class="mb-6 flex h-[40px] items-center justify-start bg-blue-20 px-4 text-lg font-bold text-black lg:bg-violet-light lg:text-violet"
					>
						Performance Metrics
					</div>

					<div class="mb-16">
						<div class="mb-4 border-y border-[#CFE3FF] py-3 text-sm lg:text-base">
							@if (isTS1) {
								TEM Safety Outcome
							} @else {
								TEM Training Effectiveness Ratings by Competency
							}
						</div>
						<app-cbta-ratings-progress-chart
							[data]="reportSummary?.chartData?.manueverChartData ?? []"
						></app-cbta-ratings-progress-chart>
					</div>

					@if (!isCC) {
						<div>
							<div class="mb-4 border-y border-[#CFE3FF] py-3 text-sm lg:text-base">Rating per Competency</div>
							<app-cbta-summary-radar-chart
								[labels]="reportSummary?.chartData?.radarChartData?.labels ?? []"
								[datasets]="reportSummary?.chartData?.radarChartData?.datasets ?? []"
							></app-cbta-summary-radar-chart>
						</div>
					}
				</mat-step>
			}

			<!-- STEP 3: Maneuver Breakdown (same pattern as QuickRatingSummaryDetails) -->
			<mat-step>
				<div id="report-maneuver-breakdown" #maneuverBreakdownRef>
					@if (isTS1) {
						<div
							class="mb-header flex h-[40px] items-center justify-start bg-blue-20 px-4 text-lg font-bold text-black lg:bg-violet-light lg:text-violet"
						>
							Maneuver Breakdown
						</div>
					}

					@if (maneuvers.length > 0) {
						<mat-accordion [multi]="true">
							@for (m of maneuvers.controls; track $index; let i = $index) {
								<mat-expansion-panel class="mat-elevation-z1 maneuver-expansion-panel" [expanded]="true">
									<!-- Header (same rules) -->
									@if (!isTS6 && !isTS1) {
										<mat-expansion-panel-header #panelH>
											<div class="ml-4">
												{{ $safeNavigationMigration(fixedManeuvers[i]?.subDescription) | uppercase }}
												<span class="ml-5">
													<span class="text-lg font-bold">&nbsp;&#183;&nbsp;</span>
													{{ fixedManeuvers[i]?.description }}
												</span>
											</div>
										</mat-expansion-panel-header>
									} @else if (isTS1) {
										<mat-expansion-panel-header #panelH class="maneuver-title">
											<mat-panel-title class="mat-panel-inner-title w-full">
												<div
													class="mb-label flex items-center justify-start bg-blue-20 px-8 text-2xl font-medium italic text-black lg:bg-violet-light lg:text-violet"
												>
													Maneuver {{ i + 1 }}: {{ m.value.maneuver.description }}
												</div>
											</mat-panel-title>
										</mat-expansion-panel-header>
									}

									<!-- Body uses the same component as Quick -->
									<app-maneuver-form
										[expensionShow]="showExpansion($any(maneuvers.at(i)))"
										maneuverAction="create-form"
										[isManeuverVisible]="false"
										[maneuverForm]="$any(maneuvers.at(i))"
									>
									</app-maneuver-form>
								</mat-expansion-panel>
							}
						</mat-accordion>
					} @else {
						<div class="p-6 italic text-gray-500">No maneuvers available.</div>
					}
				</div>
			</mat-step>
		</mat-stepper>
	}
</div>

<!-- Footer actions -->
<div class="mt-[40px] flex items-center gap-4 px-0 md:px-6">
	<button (click)="goBack()" mat-stroked-button class="min-w-[63px] border !border-b-2 !border-[#D9DBE0] !text-black">
		Back
	</button>
	@if (!isPfPmLayout && (!isLastStep || checklistMode)) {
		<button (click)="goNext()" mat-stroked-button class="min-w-[63px] border !border-b-2 !border-[#D9DBE0] !text-black">
			Next
		</button>
	}
	@if (isPfPmLayout && pfpmLayout?.showNextButton) {
		<button (click)="goNext()" mat-stroked-button class="min-w-[63px] border !border-b-2 !border-[#D9DBE0] !text-black">
			Next
		</button>
	}
	<div class="flex-1"></div>
	@if (!checklistMode && (roleService.isAdmin || roleService.isOfficeAdmin) && canDownloadSummary) {
		<button mat-stroked-button class="min-w-[63px] border !border-red !bg-red !text-white" (click)="downloadSummary()">
			Download
		</button>
	}
</div>
